/**
 * Shared "print to a popup window" helper for the folio Receipt and the
 * Invoice. Two paper formats are supported:
 *
 *   pos80  — 80mm thermal roll.
 *   letter — full Letter/A4 sheet.
 *
 * The Receipt's content is a fixed ~300px-wide card (designed for thermal
 * paper), so its 'letter' print passes a `scale` factor — CSS `zoom` blows
 * the same content up proportionally to fill a normal sheet nicely, rather
 * than printing a tiny strip in the middle of an A4 page. The Invoice's
 * content is already a full-width formal document (modelled on the
 * uploaded sample), so it prints at natural size with no scale needed.
 */
export function openPrintWindow({ title, bodyHtml, format = 'pos80', scale }) {
  const isLetter = format === 'letter';
  const w = window.open('', '', isLetter ? 'width=860,height=1000' : 'width=340,height=720');
  if (!w) return false; // popup blocked — caller can show a toast

  const pageCss = isLetter
    ? `.page{width:100%;max-width:760px;margin:0 auto;${scale ? `zoom:${scale};` : ''}}
       @page{size:letter;margin:14mm 16mm}`
    : `.page{width:272px;margin:0 auto}
       @page{size:80mm auto;margin:3mm 4mm}`;

  w.document.write(`<html><head><title>${title || ''}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800&family=Cormorant+Garamond:wght@600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
  body{font-family:'DM Sans',Arial,sans-serif;background:#fff;padding:0;font-weight:700}
  ${pageCss}
  @media print{body{padding:0}.no-print{display:none}}
</style>
</head><body><div class="page">${bodyHtml}</div></body></html>`);
  w.document.close();

  // FIX: printing immediately after document.write() races the network —
  // all text renders instantly (it's already inline HTML), but the logo
  // <img> and the Google Font need a real HTTP round-trip. Calling
  // w.print() before that finishes silently prints without them (blank
  // logo, fallback font). Wait for every <img> to finish (load or error)
  // before printing, with a timeout fallback in case an image hangs.
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    w.focus();
    w.print();
    w.close();
  };

  const images = Array.from(w.document.images || []);
  if (images.length === 0) {
    doPrint();
  } else {
    let remaining = images.length;
    const onOneDone = () => { remaining -= 1; if (remaining <= 0) doPrint(); };
    images.forEach(img => {
      if (img.complete) onOneDone();
      else { img.addEventListener('load', onOneDone); img.addEventListener('error', onOneDone); }
    });
    // Safety net: never block printing forever on a slow/broken image.
    setTimeout(doPrint, 2000);
  }

  return true;
}

/** Resolves a relative `/uploads/...` URL against the configured API origin (same convention as services/api.js). */
export function resolveAssetUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
  return `${base}${url}`;
}